import { isReportCompleted } from "@utils/reportStatus";
import { scrollToElementById } from "@utils/utils";
import React, { useState } from "react";
import { FormProvider } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { ApiReportSectionsAiGeneratedStatusChoices, ReportPageLocationEnums } from "@api/graphql/types-and-hooks";
import { ConfirmGenerationDialog } from "@components/ConfirmGenerationDialog/ConfirmGenerationDialogV2";
import { LayoutAside, LayoutBody, LayoutMain } from "@components/Layout";
import { RegenerateReportPartButton } from "@components/RegenerateReportPart/RegenerateReportPartButton";
import { ReportSection } from "@components/ReportField/ReportSection";
import { useReportForm } from "@components/ReportField/useReportForm";
import { notifyWarn } from "@components/notifications/notifications";
import { FooterButtonRow } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/FooterButtonRow";
import { StickyNav } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/ReportSummaries/StickyNav/StickyNav";
import {
  FilterSectionSubRoute,
  useGetFilteredPageSectionsByLocation,
} from "@pages/AssessmentReportV2Details/Components/helper";
import { getOrderedAssessmentReportSubRoutes } from "@pages/AssessmentReportV2Details/helpers";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { FocusedFieldManagerProvider } from "@pages/ClinicalReportDetails/Providers/FocusedFieldProvider";
import { isGeneratingSection } from "@pages/EvaluationDetails/Components/helper";
import { useReport } from "@providers/ReportProvider";

const pageLocation: FilterSectionSubRoute = "report-part-2";

interface ReportSummaryPart2SectionsProps {
  isReviewPage?: boolean;
}

export const ReportSummaryPart2: React.FC<ReportSummaryPart2SectionsProps> = ({ isReviewPage = false }) => {
  // Goals are now rendered based on TreatmentPlan field placement in template sections
  const filteredSections = useGetFilteredPageSectionsByLocation(pageLocation);

  const navSections = filteredSections.map((section) => ({
    title: section.title,
    aiGeneratedStatus: section.aiGeneratedStatus,
  }));
  const { pathname } = useLocation();
  const go = useNavigate();
  const { getNextRoute } = getOrderedAssessmentReportSubRoutes();
  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as InitialAssessmentPageRoute) ||
    ("upload-files" as InitialAssessmentPageRoute);
  const nextPage = getNextRoute(currentRoute) as FilterSectionSubRoute;
  const nextPageFilteredSections = useGetFilteredPageSectionsByLocation(nextPage);
  const [promptDialogOpen, setPromptDialogOpen] = React.useState<boolean>(false);
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);

  const methods = useReportForm(filteredSections);

  const { report } = useReport();
  const reportIsCompleted = isReportCompleted(report);
  const isSectionGenerating = isGeneratingSection(filteredSections);
  const nextPageAlreadyGenerated = nextPageFilteredSections.some(
    (section) =>
      section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending ||
      section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted,
  );

  const handlePrimaryButtonAction = methods.handleSubmit(
    () => {
      setShowRequiredErrors(false);
      if (!nextPageAlreadyGenerated) setPromptDialogOpen(true);
      else go(`../${nextPage}`);
    },
    (errors) => {
      setShowRequiredErrors(true);
      Object.values(errors).forEach((error) => error?.message && notifyWarn(String(error.message)));
      const firstErrorId = Object.keys(errors)[0];
      if (firstErrorId) scrollToElementById(firstErrorId, "center");
    },
  );

  if (isReviewPage) {
    return (
      <>
        {filteredSections.map((section) => (
          <ReportSection key={section.id} section={section} disabled={reportIsCompleted} />
        ))}
      </>
    );
  }

  return (
    <FormProvider {...methods}>
      <FocusedFieldManagerProvider showRequiredErrors={showRequiredErrors}>
        <LayoutBody>
          <LayoutMain className="py-4">
            {filteredSections.map((section) => (
              <ReportSection key={section.id} section={section} disabled={reportIsCompleted} />
            ))}
          </LayoutMain>
          <LayoutAside>
            <StickyNav sections={navSections} />
          </LayoutAside>
        </LayoutBody>
        <FooterButtonRow
          primaryButtonDisabled={isSectionGenerating}
          primaryButtonDisabledTooltipReason="All sections on the page must finish generating before continuing"
          primaryButtonAction={handlePrimaryButtonAction}
          skipNavigate
          leadingActions={
            <RegenerateReportPartButton
              show={!reportIsCompleted}
              disabled={isSectionGenerating}
              pageLocation={ReportPageLocationEnums.ReportPart_2}
              ConfirmGenerationDialog={ConfirmGenerationDialog}
            />
          }
        />

        <ConfirmGenerationDialog
          type={ReportPageLocationEnums.ReportPart_2}
          promptDialogOpen={promptDialogOpen}
          setPromptDialogOpen={setPromptDialogOpen}
        />
      </FocusedFieldManagerProvider>
    </FormProvider>
  );
};
